import React, { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS } from './config/api';
import ProtocolStats from './components/ProtocolStats';
import AnimatedBurnCounter from './components/AnimatedBurnCounter';
import TokenDonut from './components/TokenDonut';
import ProviderGrid from './components/ProviderGrid';
import Leaderboard from './components/Leaderboard';
import ProviderDetail from './components/ProviderDetail';
import ActivityFeed from './components/ActivityFeed';
import NetworkViz from './components/NetworkViz';
import SettlementChart from './components/SettlementChart';
import ProviderHealth from './components/ProviderHealth';
import ProviderPortal from './components/ProviderPortal';
import TermsOfService from './components/TermsOfService';

function DevnetBanner() {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="bg-amber-900/30 border-b border-amber-700/30 px-3 py-2 flex items-center justify-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs">
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0"></span>
      <span className="text-amber-300 font-medium whitespace-nowrap">Solana Devnet</span>
      <span className="text-amber-500/70 hidden sm:inline">—</span>
      <span className="text-amber-400/70 hidden sm:inline">Mainnet Coming Soon</span>
      <button onClick={() => setDismissed(true)} className="ml-2 text-amber-600 hover:text-amber-400 transition-colors text-sm leading-none flex-shrink-0">&times;</button>
    </div>
  );
}

// Generate activity events from protocol state changes
function generateActivities(stats, providers, prevStats) {
  const events = [];
  const now = Date.now();

  if (providers && providers.length > 0) {
    providers.forEach(p => {
      if (p.total_calls > 0) {
        events.push({
          type: 'settlement',
          message: `${p.name} settled ${p.total_calls} tool calls`,
          timestamp: now - Math.random() * 300000,
          amount: `${p.total_calls} calls`,
        });
      }
      if (p.active_disputes > 0) {
        events.push({
          type: 'dispute',
          message: `${p.active_disputes} active dispute${p.active_disputes > 1 ? 's' : ''} against ${p.name}`,
          timestamp: now - Math.random() * 600000,
        });
      }
    });
    providers.forEach(p => {
      events.push({
        type: 'register',
        message: `${p.name} registered as provider`,
        timestamp: now - 86400000 + Math.random() * 3600000,
      });
    });
  }

  if (stats?.total_thane_burned > 0) {
    events.push({
      type: 'burn',
      message: `${stats.total_thane_burned} THANE lamports burned via settlement fees`,
      timestamp: now - Math.random() * 120000,
      amount: `${stats.total_thane_burned} burned`,
    });
  }

  events.sort((a, b) => b.timestamp - a.timestamp);
  return events.slice(0, 20);
}

export default function App() {
  const [stats, setStats] = useState(null);
  const [burn, setBurn] = useState(null);
  const [supply, setSupply] = useState(null);
  const [providers, setProviders] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activities, setActivities] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [searchTags, setSearchTags] = useState('');
  const [tab, setTab] = useState('overview');
  const [showTOS, setShowTOS] = useState(false); // overview | marketplace | leaderboard

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, burnRes, provRes, lbRes] = await Promise.all([
        fetch(ENDPOINTS.PROTOCOL_STATS),
        fetch(ENDPOINTS.BURN_TRACKER),
        fetch(`${ENDPOINTS.DISCOVER}${searchTags ? `?tags=${searchTags}` : ''}`),
        fetch(ENDPOINTS.LEADERBOARD),
      ]);
      const newStats = await statsRes.json();
      const newBurn = await burnRes.json();
      const provData = await provRes.json();
      const lbData = await lbRes.json();
      const provList = provData.providers || [];

      setStats(newStats);
      setBurn(newBurn);
      setSupply({ uiAmount: newStats.thane_supply || 1_000_000_000 });
      setProviders(provList);
      setLeaderboard(lbData.providers || []);
      setActivities(generateActivities(newStats, provList, stats));
    } catch (e) {
      console.error('Failed to fetch THANE data:', e);
    }
  }, [searchTags]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden">
      <DevnetBanner />
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center font-bold text-sm shadow-lg shadow-purple-500/20">T</div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">THANE Protocol</h1>
              <p className="text-[10px] text-slate-500 -mt-0.5">Decentralized MCP Tool Marketplace</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center gap-3 text-xs text-slate-400">
              <span className="flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                {stats?.active_providers || 0} Providers
              </span>
              <span className="text-slate-700">|</span>
              <span>{stats?.total_calls_settled?.toLocaleString() || 0} Calls Settled</span>
              <span className="text-slate-700">|</span>
              <span className="text-orange-400">{stats?.total_thane_burned || 0} Burned</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        {/* Tab Navigation */}
        <div className="flex items-center gap-1 bg-slate-900/60 rounded-lg p-1 border border-slate-800 w-full sm:w-fit overflow-x-auto scrollbar-thin">
          {['overview', 'marketplace', 'leaderboard', 'providers'].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex-shrink-0 px-3 sm:px-4 py-2 sm:py-1.5 text-[11px] sm:text-xs font-medium rounded-md transition-all text-center whitespace-nowrap ${
                tab === t
                  ? 'bg-purple-600 text-white shadow-sm'
                  : 'text-slate-400 hover:text-slate-200'
              }`}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {tab === 'overview' && (
          <>
            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              <ProtocolStats stats={stats} />
              <AnimatedBurnCounter burn={burn} />
            </div>

            {/* Middle Row: Network + Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <NetworkViz providers={providers} stats={stats} />
              <ActivityFeed activities={activities} />
            </div>

            {/* Bottom Row: Charts + Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              <SettlementChart />
              <TokenDonut supply={supply} />
            </div>

            {/* Provider Health */}
            <ProviderHealth providers={providers} />
          </>
        )}

        {/* Marketplace Tab */}
        {tab === 'marketplace' && (
          <>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchTags}
                onChange={(e) => setSearchTags(e.target.value)}
                placeholder="Search by tags: bitcoin, market-data, sentiment..."
                className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-purple-500/50"
                onKeyDown={(e) => e.key === 'Enter' && fetchData()}
              />
              <button
                onClick={fetchData}
                className="px-4 py-2.5 bg-purple-600 hover:bg-purple-500 rounded-lg text-sm font-medium transition-colors"
              >
                Search
              </button>
            </div>
            <ProviderGrid providers={providers} onSelect={setSelectedProvider} />
          </>
        )}

        {/* Leaderboard Tab */}
        {tab === 'leaderboard' && <Leaderboard providers={leaderboard} />}

        {/* Providers Tab (Signup/Login/Dashboard) */}
        {tab === 'providers' && <ProviderPortal />}

        {/* Provider Detail Modal */}
        {selectedProvider && (
          <ProviderDetail provider={selectedProvider} onClose={() => setSelectedProvider(null)} />
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-8 sm:mt-12 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 flex flex-col sm:flex-row items-center gap-2 sm:justify-between text-[10px] sm:text-xs text-slate-600">
          <span>THANE Protocol v0.4 &mdash; Valhalla Systems LLC</span>
          <div className="flex items-center gap-2">
            {stats?.source === 'solana-devnet' && (
              <span className="flex items-center gap-1 text-emerald-500">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                Solana Devnet
              </span>
            )}
            <span className="text-slate-700">|</span>
            <button onClick={() => setShowTOS(true)} className="text-slate-600 hover:text-purple-400 transition-colors">Terms of Service</button>
            <span className="text-slate-700">|</span>
            <span>&ldquo;The protocol that grants agents access.&rdquo;</span>
          </div>
        </div>
      </footer>

      {showTOS && <TermsOfService onClose={() => setShowTOS(false)} />}

      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slideIn { animation: slideIn 0.3s ease-out; }
        .scrollbar-thin::-webkit-scrollbar { width: 4px; }
        .scrollbar-thin::-webkit-scrollbar-track { background: transparent; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #334155; border-radius: 4px; }
      `}</style>
    </div>
  );
}
